import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX, FiExternalLink } from 'react-icons/fi';

/**
 * Modal con una demo real embebida (onboarding / conexión segura).
 *
 * El iframe SOLO existe mientras el modal está abierto: el componente entero
 * devuelve null si no lo está, así que no se descarga nada (son apps Flutter de
 * varios MB) hasta que el usuario lo pide.
 *
 * Accesibilidad y navegación:
 *  - role="dialog" + aria-modal, cerrado con Escape y con clic en el fondo.
 *  - El foco entra al abrir y VUELVE al elemento que lo abrió al cerrar.
 *  - Se bloquea el scroll del fondo mientras está abierto y se restaura después
 *    (guardando el overflow previo, no asumiendo que era '').
 *  - Reutiliza la clase `body.discovery-open` que ya esconde el FAB de WhatsApp,
 *    que si no se planta encima de la zona de enviar del chat.
 */
const DemoModal = ({ open, url, title, onClose }) => {
   const { t } = useTranslation();
   const closeRef = useRef(null);
   const previouslyFocused = useRef(null);

   useEffect(() => {
      if (!open) return undefined;

      previouslyFocused.current = document.activeElement;
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('discovery-open');

      const onKey = (e) => {
         if (e.key === 'Escape') onClose();
      };
      document.addEventListener('keydown', onKey);

      // El foco al botón de cerrar: es la salida y evita quedarse fuera del modal.
      const focusTimer = setTimeout(() => {
         if (closeRef.current) closeRef.current.focus();
      }, 0);

      return () => {
         document.removeEventListener('keydown', onKey);
         clearTimeout(focusTimer);
         document.body.style.overflow = prevOverflow;
         document.body.classList.remove('discovery-open');
         if (previouslyFocused.current && previouslyFocused.current.focus) {
            previouslyFocused.current.focus();
         }
      };
   }, [open, onClose]);

   if (!open || !url) return null;

   return (
      <div
         className="demo-modal"
         role="dialog"
         aria-modal="true"
         aria-label={title}
         onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
         }}
      >
         <div className="demo-modal__panel">
            <header className="demo-modal__head">
               <h3>{title}</h3>
               <div className="demo-modal__head-actions">
                  <a href={url} target="_blank" rel="noreferrer" className="demo-modal__external">
                     {t('demo_modal_new_tab')} <FiExternalLink size={13} />
                  </a>
                  <button
                     type="button"
                     className="demo-modal__close"
                     onClick={onClose}
                     ref={closeRef}
                     aria-label={t('demo_modal_close')}
                  >
                     <FiX size={18} />
                  </button>
               </div>
            </header>
            <iframe src={url} title={title} className="demo-modal__iframe" allow="clipboard-write" />
         </div>
      </div>
   );
};

export default DemoModal;
